import type { PathParam } from "react-router";
import { route } from "./route";

/**
 * The route registry: the single source of truth for paths.
 *
 * Paths are the canonical, best-practice targets, chosen to be correct rather than
 * to match the live router. Conventions: lowercase kebab-case segments, plural
 * collections with a singular id beneath them (`/sections/:sectionId/items/:itemId`),
 * `:resourceId` params, no filler or UI-artefact segments.
 *
 * Migrating the live router to these paths is done per feature. Legacy external
 * URLs (OAuth, emailed links) are handled with redirects to the new paths, so even
 * the `external` routes use the canonical names. This module is the definition
 * only; do not navigate with these builders until a feature's live routes have
 * been renamed to match.
 */
export const routes = {
  welcome: route("/welcome"),

  home: route("/home", { tab: "home", requiresAuth: true }),

  itineraries: route("/itineraries", { tab: "home", requiresAuth: true }),
  // The org's itineraries the user is not in, behind the seeAllItineraries claim.
  otherItineraries: {
    list: route("/itineraries/others", { tab: "home", requiresAuth: true }),
    tag: route("/itineraries/others/tags/:tagId", { tab: "home", requiresAuth: true }),
  },

  chat: {
    list: route("/chat", { tab: "chat", requiresAuth: true }),
    // modalMatch keeps info / members / notifications / manage pushing inside the room
    // sheet rather than dismissing it.
    room: route("/chat/:roomType/:roomId", {
      tab: "chat",
      requiresAuth: true,
      present: "pageSheet",
      modalMatch: ["/chat/:roomType/:roomId/*rest"],
    }),
    // These live in the room sheet's stack (see room's modalMatch), not the chat tab's,
    // so selecting `tab` would not surface them.
    info: route("/chat/:roomType/:roomId/info", { tab: "chat", requiresAuth: true, allowDuplicate: true }),
    members: route("/chat/:roomType/:roomId/info/members", {
      tab: "chat",
      requiresAuth: true,
      allowDuplicate: true,
    }),
    notifications: route("/chat/:roomType/:roomId/info/notifications", {
      tab: "chat",
      requiresAuth: true,
      allowDuplicate: true,
    }),
    manage: route("/chat/:roomType/:roomId/manage", { tab: "chat", requiresAuth: true, allowDuplicate: true }),
    new: route("/chat/new", { tab: "chat", requiresAuth: true, present: "formSheet" }),
  },

  team: {
    page: route("/team", { tab: "team", requiresAuth: true }),
    search: route("/team/search", { tab: "team", requiresAuth: true }),
    categories: route("/team/categories", { tab: "team", requiresAuth: true }),
    // modalMatch is scoped to `/team/members/*`, not the global `/chat/*`, because
    // modalMatch is global: the navigator checks a push target against every open modal's
    // paths, so aliasing `/chat/*` here would present chat anywhere as a member pageSheet.
    member: route("/team/members/:userId", {
      tab: "team",
      requiresAuth: true,
      present: "pageSheet",
      modalMatch: ["/team/members/*rest"],
    }),
  },

  account: {
    page: route("/account", { tab: "account", requiresAuth: true }),
    profile: route("/account/profile", { tab: "account", requiresAuth: true }),
    // The picker path stays under this sheet's modalMatch, not sectionSelection's /edit/*rest.
    // Delete opens from this sheet, so it stays in the sheet's stack rather than dismissing it.
    profileEdit: route("/account/profile/edit", {
      tab: "account",
      requiresAuth: true,
      present: "pageSheet",
      modalMatch: ["/account/profile/edit/*rest", "/account/delete"],
    }),
    // edit.dateTimePicker's same page, under this sheet's path so it matches the modalMatch above.
    dateTimePicker: route("/account/profile/edit/date-time-picker/:sectionId/:itemId/:fieldId", {
      tab: "account",
      requiresAuth: true,
    }),
    // Under this sheet's path so it matches profileEdit's modalMatch.
    profilePickupAddress: route("/account/profile/edit/pickup-address/:fieldName", {
      tab: "account",
      requiresAuth: true,
    }),
    delete: route("/account/delete", { tab: "account", requiresAuth: true }),
    organisations: route("/account/organisations", { tab: "account", requiresAuth: true }),
    accessRequests: route("/account/access-requests", { tab: "account", requiresAuth: true }),
    subscription: route("/account/subscription", { tab: "account", requiresAuth: true }),
    // The itineraries list as reached from Account: beside Account's menu in two panes, where
    // the top-level `itineraries` opened from the tab bar's menu is a sheet.
    itineraries: route("/account/itineraries", { tab: "account", requiresAuth: true }),
    otherItineraries: {
      list: route("/account/itineraries/others", { tab: "account", requiresAuth: true }),
      tag: route("/account/itineraries/others/tags/:tagId", { tab: "account", requiresAuth: true }),
    },

    settings: {
      page: route("/account/settings", { tab: "account", requiresAuth: true }),
      about: route("/account/settings/about", { tab: "account", requiresAuth: true }),
      licences: route("/account/settings/about/licences", { tab: "account", requiresAuth: true }),
      credits: route("/account/settings/about/credits", { tab: "account", requiresAuth: true }),
      themes: route("/account/settings/themes", { tab: "account", requiresAuth: true }),
      booking: route("/account/settings/booking", { tab: "account", requiresAuth: true }),
      language: route("/account/settings/language", { tab: "account", requiresAuth: true }),
      time: route("/account/settings/time", { tab: "account", requiresAuth: true }),
      calendar: route("/account/settings/calendar", { tab: "account", requiresAuth: true }),
      widgets: route("/account/settings/widgets", { tab: "account", requiresAuth: true }), // iOS-only home-screen widget settings
      homeScreen: route("/account/settings/home-screen", { tab: "account", requiresAuth: true }),
      team: route("/account/settings/team", { tab: "account", requiresAuth: true }),
      notifications: route("/account/settings/notifications", { tab: "account", requiresAuth: true }),
      localNotifications: route("/account/settings/notifications/local", { tab: "account", requiresAuth: true }),
      localNotificationsPending: route("/account/settings/notifications/local/pending", {
        tab: "account",
        requiresAuth: true,
      }),
      chatNotifications: route("/account/settings/notifications/chat", { tab: "account", requiresAuth: true }),
    },
  },

  edit: {
    sectionSelection: route("/edit", { requiresAuth: true, present: "pageSheet", modalMatch: ["/edit/*rest"] }),
    publicItineraries: route("/edit/public-itineraries", { requiresAuth: true }),
    members: route("/edit/members", { requiresAuth: true }),
    // AddTeamMember (the same page as routes.setup.team) is also pushed from inside
    // the /edit sheet (edit-members.tsx). Its own /edit/-prefixed path falls under
    // sectionSelection's "/edit/*rest" modalMatch and stays in that sheet, while the
    // plain Team tab entry opens the page as its own pageSheet (routes.setup.team).
    membersAdd: route("/edit/members/add", { requiresAuth: true }),

    section: route("/edit/sections/:sectionId", { requiresAuth: true }),
    sectionItem: route("/edit/sections/:sectionId/items/:itemId", { requiresAuth: true }),
    sectionItemPage: route("/edit/sections/:sectionId/items/:itemId/pages/:pageId", { requiresAuth: true }),
    sectionItemPageItem: route("/edit/sections/:sectionId/items/:itemId/pages/:pageId/items/:subItemId", {
      requiresAuth: true,
    }),

    // One generic pushed screen for every date/time field type; each field's wrapper decides
    // whether to push here or open its own drawer. Itinerary-edit callers only - the
    // date-of-birth field pushes account.dateTimePicker instead, an alias of this same
    // page under a path that doesn't fall under this sheet's modalMatch (see there).
    dateTimePicker: route("/edit/date-time-picker/:sectionId/:itemId/:fieldId", { requiresAuth: true }),

    transportList: route("/edit/transport", { requiresAuth: true }),
    transportItem: route("/edit/transport/:itemId", { requiresAuth: true }),
    transportPickUpAddress: route("/edit/transport/:itemId/pick-up-address", { requiresAuth: true }),
    transportDropOffAddress: route("/edit/transport/:itemId/drop-off-address", { requiresAuth: true }),
    transportBookings: route("/edit/transport/:itemId/bookings", { requiresAuth: true }),
    transportBooking: route("/edit/transport/:itemId/bookings/:bookingId", { requiresAuth: true }),

    activitiesList: route("/edit/activities", { requiresAuth: true }),
    activityItem: route("/edit/activities/:itemId", { requiresAuth: true }),
    activityAddress: route("/edit/activities/:itemId/address", { requiresAuth: true }),
    activityBookings: route("/edit/activities/:itemId/bookings", { requiresAuth: true }),
    activityBooking: route("/edit/activities/:itemId/bookings/:bookingId", { requiresAuth: true }),

    todoItem: route("/edit/todos/:itemId", { requiresAuth: true }),
    todoBookings: route("/edit/todos/:itemId/bookings", { requiresAuth: true }),
    todoBooking: route("/edit/todos/:itemId/bookings/:bookingId", { requiresAuth: true }),

    eventInfo: route("/edit/event-info", { requiresAuth: true }),
    eventInfoAddress: route("/edit/event-info/address", { requiresAuth: true }),

    hotelSearch: route("/edit/hotel-booking/search", { requiresAuth: true }),
    hotelDetails: route("/edit/hotel-booking/details", { requiresAuth: true }),
    hotelAvailability: route("/edit/hotel-booking/availability", { requiresAuth: true }),
    hotelReturn: route("/edit/hotel-booking/return", { requiresAuth: true }),
    hotelManage: route("/edit/hotel-booking/:itemId/manage", { requiresAuth: true }),

    expenses: route("/edit/expenses", { requiresAuth: true }),
    expenseNewStandard: route("/edit/expenses/new/standard", { requiresAuth: true }),
    expenseNewDistance: route("/edit/expenses/new/distance", { requiresAuth: true }),
    expenseNewDistanceAddress: route("/edit/expenses/new/distance/address/:fieldName", { requiresAuth: true }),
    expenseEditStandard: route("/edit/expenses/:expenseId/standard", { requiresAuth: true }),
    expenseEditDistance: route("/edit/expenses/:expenseId/distance", { requiresAuth: true }),
    expenseEditDistanceAddress: route("/edit/expenses/:expenseId/distance/address/:fieldName", { requiresAuth: true }),
    expenseEditStandardAIResult: route("/edit/expenses/:expenseId/standard/aiResult", { requiresAuth: true }),
  },

  item: {
    flight: route("/items/flights/:itemId", { requiresAuth: true }),
    todo: route("/items/todos/:itemId", { requiresAuth: true }),
    activity: route("/items/activities/:itemId", { requiresAuth: true }),
    hotel: route("/items/hotels/:itemId", { requiresAuth: true }),
    hireCar: route("/items/hire-cars/:itemId", { requiresAuth: true }),
    transport: route("/items/transport/:itemId", { requiresAuth: true }),
    parking: route("/items/parking/:itemId", { requiresAuth: true }),
  },

  /**
   * Screens the deeplink resolver navigates to when it cannot open a link
   * (see feat/deeplink/pendingDeeplink.ts). Nothing in the UI links here.
   */
  link: {
    noAccess: route("/link/no-access", { requiresAuth: true }),
  },

  // The first screen of a tab's trailing split column under iOS native navigation: what the
  // trailing pane shows before an entry is chosen. Only NativeRootController opens it.
  panes: {
    trailingRoot: route("/panes/:tabId", { requiresAuth: true }),
  },

  section: {
    items: route("/sections/:sectionId", { requiresAuth: true }),
    categoryItems: route("/sections/:sectionId/categories/:categoryId", { requiresAuth: true }),
  },

  /** Every favourited entry, rendered by the section item list (see sections.routes.tsx). */
  favourites: route("/favourites", { requiresAuth: true }),

  /** The selected itinerary's notes, those on one item, and one note being written or read. */
  notes: {
    list: route("/notes", { requiresAuth: true }),
    item: route("/notes/items/:entryId", { requiresAuth: true }),
    edit: route("/notes/edit/:noteId", { requiresAuth: true }),
  },

  activityFeed: route("/activity-feed", { requiresAuth: true, present: "pageSheet" }),
  clocks: route("/clocks", { tab: "home", requiresAuth: true, present: "pageSheet" }),
  // Clive's guided create. The form it falls back to is routes.edit.eventInfo with ?new=true.
  createItinerary: route("/create-itinerary", {
    tab: "home",
    requiresAuth: true,
    present: "fullScreen",
    // An edit screen opened from it, such as a hotel, an invite or a section, stays in its
    // stack, so Back returns to it.
    modalMatch: ["/edit/*rest"],
    hidesTabBar: true,
    hidesNavBar: true,
  }),
  notificationsCalendar: route("/notifications-calendar", { requiresAuth: true }),
  // The terms gate presents as a pageSheet on iOS (see checkTermsAndConditions); on the
  // other platforms the gate is an overlay dialog, so the page is reached directly only.
  termsOfService: route("/terms-of-service", { requiresAuth: true, present: "pageSheet" }),

  setup: {
    team: route("/setup/team", { requiresAuth: true, present: "pageSheet" }),
  },

  guestDuplicate: {
    team: route("/guest-duplicate/team"),
    itinerary: route("/guest-duplicate/itinerary"),
    dates: route("/guest-duplicate/dates"),
  },

  // Legacy emailed and OAuth URLs redirect to these paths, so external routes use
  // the canonical names too.
  auth: {
    signIn: route("/auth/sign-in", { external: true, authFlow: true }),
    signInEmailSent: route("/auth/sign-in/email-sent", { external: true, authFlow: true }),
    forgotPassword: route("/auth/forgot-password", { external: true, authFlow: true }),
    forgotPasswordEmailSent: route("/auth/forgot-password/email-sent", { external: true, authFlow: true }),
    signUp: route("/auth/sign-up", { external: true, authFlow: true }),
    signUpEmailSent: route("/auth/sign-up/email-sent", { external: true, authFlow: true }),
    signUpDetails: route("/auth/sign-up/details", { external: true, authFlow: true }),
    signUpPassword: route("/auth/sign-up/password", { external: true, authFlow: true }),
    signUpProfilePicture: route("/auth/sign-up/profile-picture", { external: true, authFlow: true }),
    terms: route("/auth/terms", { external: true, authFlow: true }),
    confirmEmail: route("/auth/confirm-email", { external: true, authFlow: true }),
    organisation: route("/auth/organisation", { external: true, authFlow: true }),
    organisationDetails: route("/auth/organisation/details", { external: true, authFlow: true }),
    teamName: route("/auth/team-name", { external: true, authFlow: true }),
    requestAccess: route("/auth/request-access", { external: true, authFlow: true }),
    requestAccessEmailSent: route("/auth/request-access/email-sent", { external: true, authFlow: true }),
    complete: route("/auth/complete", { external: true, authFlow: true }),
    notSameUser: route("/auth/not-same-user", { external: true, authFlow: true }),
    joined: route("/auth/joined", { external: true, authFlow: true }),
    join: route("/auth/join", { external: true, authFlow: true }),
  },

  oauthCallback: route("/oauth/callback", { external: true, authFlow: true }),
  guestResolver: route("/guest/:token", { external: true, hidesNavBar: true }),

  dev: {
    menu: route("/dev", { hidesTabBar: true }),
    logs: route("/dev/logs", { hidesTabBar: true }),
    time: route("/dev/time", { hidesTabBar: true }),
    env: route("/dev/env", { hidesTabBar: true }),
    store: route("/dev/store", { hidesTabBar: true }),
    localExpenses: route("/dev/local-expenses", { hidesTabBar: true }),
    mapStart: route("/dev/map-start", { hidesTabBar: true, hidesNavBar: true }),
  },
};

/**
 * A structural match for a route node (has a `path` and a `build`). Used to tell
 * leaf routes apart from grouping objects when flattening the registry.
 */
type AnyRouteNode = { readonly path: string; build: (...args: any[]) => string };

/** Every route node in the registry, regardless of nesting depth. */
type LeafRoutes<T> = T extends AnyRouteNode
  ? T
  : T extends object
    ? { [K in keyof T]: LeafRoutes<T[K]> }[keyof T]
    : never;

/**
 * The `Pages` map React Router's `Register` interface expects, derived from the
 * registry so there is no second list to maintain. Augmenting `Register` makes
 * `href()` and `<Link to={href(...)}>` type-checked against these paths.
 */
type Pages = {
  [R in LeafRoutes<typeof routes> as R["path"]]: {
    params: Record<PathParam<R["path"]>, string>;
  };
};

declare module "react-router" {
  interface Register {
    pages: Pages;
  }
}

/**
 * The union of every registered path template, e.g. `"/home" | "/chat/:roomType/:roomId" | …`.
 * Navigation helpers take this as their first argument, so call sites pass a path
 * string (autocompleted, typo-checked) without importing `routes`.
 */
export type RoutePath = keyof Pages;
